import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Eye, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ImagesField } from "@/pages/admin/product/ImagesField";
import { cleanVariants, VariantsField } from "@/pages/admin/product/VariantsField";
import { VersionsPanel } from "@/pages/admin/product/VersionsPanel";
import { PreviewDialog } from "@/pages/admin/product/PreviewDialog";

const EMPTY = { name: "", slug: "", sku: "", category: "", short_description: "", description: "", price: 0, discount_percent: 0,
  hpp: 0, stock: 0, low_stock_threshold: 5, weight_grams: 200, length_cm: 0, width_cm: 0, height_cm: 0, images: [], video_url: "",
  tags: [], status: "draft", seo_title: "", seo_description: "", seo_keywords: "", variants: [], featured: false };
const FIELDS = Object.keys(EMPTY);
const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const Card = ({ title, children }) => (
  <section className="space-y-4 rounded-lg border border-stone-200 bg-white p-4 md:p-5"><h2 className="font-semibold">{title}</h2>{children}</section>
);
const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-xs text-stone-500">{hint}</p>}</div>
);
const Num = ({ label, k, form, set, step = "1", hint }) => (
  <Field label={label} hint={hint}><Input type="number" min="0" step={step} data-testid={`product-form-${k}`} value={form[k]} onChange={(e) => set({ [k]: Number(e.target.value) })} /></Field>
);

const CategorySelect = ({ cats, value, onChange }) => {
  const tops = cats.filter((c) => !c.parent_id);
  return (
    <select data-testid="product-form-category" required value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm">
      <option value="">Pilih kategori</option>
      {tops.map((t) => [
        <option key={t.id} value={t.id}>{t.name}</option>,
        ...cats.filter((c) => c.parent_id === t.id).map((c) => <option key={c.id} value={c.id}>{"\u00a0\u00a0\u203a "}{c.name}</option>),
      ])}
    </select>
  );
};

const TagPicker = ({ tags, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {tags.map((t) => {
      const on = value.includes(t.id);
      return <button type="button" key={t.id} data-testid={`product-tag-${t.id}`} onClick={() => onChange(on ? value.filter((x) => x !== t.id) : [...value, t.id])}
        className={`rounded-full border px-3 py-1 text-xs ${on ? "border-amber-600 bg-amber-50 text-amber-900" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{t.name}</button>;
    })}
    {tags.length === 0 && <p className="text-xs text-stone-500">Belum ada tag. Buat di menu Kategori & Tag.</p>}
  </div>
);

export default function ProductEditor() {
  const { id } = useParams();
  const isNew = !id;
  const call = useApi();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [cats, setCats] = useState([]);
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [preview, setPreview] = useState(false);
  const [versionKey, setVersionKey] = useState(0);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const load = (p) => { setForm(Object.fromEntries(FIELDS.map((k) => [k, p[k] ?? EMPTY[k]]))); setSlugTouched(true); };

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/tags").then((r) => setTags(r.data));
  }, []);
  useEffect(() => {
    if (isNew) return;
    call("get", `/admin/products/${id}`).then(load).catch(() => toast.error("Produk tidak ditemukan")).finally(() => setLoading(false));
  }, [id, isNew, call]);

  const autoSlug = slugTouched ? form.slug : slugify(form.name);
  const plain = useMemo(() => form.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(), [form.description]);
  const catName = cats.find((c) => c.id === form.category)?.name || "";

  const save = async (e) => {
    e.preventDefault();
    if (!form.images.length) return toast.error("Minimal 1 foto produk");
    setSaving(true);
    try {
      const saved = await call(isNew ? "post" : "put", isNew ? "/admin/products" : `/admin/products/${id}`,
        { ...form, slug: autoSlug, variants: cleanVariants(form.variants) });
      toast.success("Produk disimpan");
      if (isNew) navigate(`/admin/produk/${saved.id}`, { replace: true }); else { load(saved); setVersionKey((k) => k + 1); }
    } catch (err) { toast.error(errMsg(err, "Gagal menyimpan")); } finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-stone-500">Memuat...</p>;

  return (
    <form onSubmit={save} className="space-y-5" data-testid="product-editor">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin/produk" className="flex items-center gap-1 text-sm text-stone-500 hover:text-amber-700" data-testid="product-editor-back"><ChevronLeft className="h-4 w-4" /> Katalog</Link>
        <h1 className="text-2xl font-semibold">{isNew ? "Tambah Produk" : "Edit Produk"}</h1>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" onClick={() => setPreview(true)} data-testid="product-preview-button"><Eye className="mr-1.5 h-4 w-4" /> Pratinjau</Button>
          <Button type="submit" disabled={saving} data-testid="product-form-submit">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button>
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card title="Informasi produk">
            <Field label="Nama produk (maks. 100 karakter)"><Input required maxLength={100} data-testid="product-form-name" value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Slug URL" hint="Terisi otomatis dari nama, boleh diubah."><Input data-testid="product-form-slug" value={autoSlug} onChange={(e) => { setSlugTouched(true); set({ slug: slugify(e.target.value) }); }} /></Field>
              <Field label="SKU (unik)"><Input required maxLength={64} data-testid="product-form-sku" value={form.sku} onChange={(e) => set({ sku: e.target.value.toUpperCase() })} /></Field>
            </div>
            <Field label={`Deskripsi singkat (${form.short_description.length}/200)`}><Textarea maxLength={200} rows={2} data-testid="product-form-short-description" value={form.short_description} onChange={(e) => set({ short_description: e.target.value })} /></Field>
            <Field label="Deskripsi lengkap"><RichTextEditor value={form.description} onChange={(description) => set({ description })} /></Field>
          </Card>
          <Card title="Foto & video"><ImagesField images={form.images} onChange={(images) => set({ images })} />
            <Field label="Video produk (URL YouTube, opsional)"><Input placeholder="https://www.youtube.com/watch?v=..." data-testid="product-form-video" value={form.video_url} onChange={(e) => set({ video_url: e.target.value.trim() })} /></Field>
          </Card>
          <Card title="Harga & stok">
            <div className="grid gap-4 sm:grid-cols-3">
              <Num label="Harga jual (Rp)" k="price" form={form} set={set} />
              <Num label="Diskon (%)" k="discount_percent" form={form} set={set} />
              <Num label="Harga beli / HPP (Rp)" k="hpp" form={form} set={set} hint="Privat, hanya terlihat admin." />
              <Num label="Stok" k="stock" form={form} set={set} />
              <Num label="Batas stok menipis" k="low_stock_threshold" form={form} set={set} />
            </div>
            <VariantsField variants={form.variants} onChange={(variants) => set({ variants })} />
          </Card>
          <Card title="Pengiriman">
            <div className="grid gap-4 sm:grid-cols-4">
              <Num label="Berat (gram)" k="weight_grams" form={form} set={set} />
              <Num label="Panjang (cm)" k="length_cm" form={form} set={set} step="0.1" />
              <Num label="Lebar (cm)" k="width_cm" form={form} set={set} step="0.1" />
              <Num label="Tinggi (cm)" k="height_cm" form={form} set={set} step="0.1" />
            </div>
          </Card>
        </div>
        <div className="space-y-5">
          <Card title="Status & kategori">
            <Field label="Status">
              <select data-testid="product-form-status" value={form.status} onChange={(e) => set({ status: e.target.value })} className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="draft">Draft (belum tampil)</option><option value="published">Published (tampil di toko)</option><option value="archived">Archived (diarsipkan)</option>
              </select>
            </Field>
            <Field label="Kategori"><CategorySelect cats={cats} value={form.category} onChange={(category) => set({ category })} /></Field>
            <Field label="Tag / label"><TagPicker tags={tags} value={form.tags} onChange={(t) => set({ tags: t })} /></Field>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => set({ featured: v })} data-testid="product-form-featured" /> Produk unggulan</label>
          </Card>
          <Card title="SEO">
            <Field label="Meta title"><Input maxLength={120} placeholder={form.name} data-testid="product-form-seo-title" value={form.seo_title} onChange={(e) => set({ seo_title: e.target.value })} /></Field>
            <Field label="Meta description"><Textarea rows={3} maxLength={300} placeholder={(form.short_description || plain).slice(0, 160)} data-testid="product-form-seo-description" value={form.seo_description} onChange={(e) => set({ seo_description: e.target.value })} /></Field>
            <Field label="Keywords" hint="Kosongkan agar terisi otomatis dari nama & tag."><Input maxLength={300} data-testid="product-form-seo-keywords" value={form.seo_keywords} onChange={(e) => set({ seo_keywords: e.target.value })} /></Field>
          </Card>
          {!isNew && <VersionsPanel productId={id} reloadKey={versionKey} onRestored={load} />}
        </div>
      </div>
      <PreviewDialog open={preview} onOpenChange={setPreview} form={form} categoryName={catName} />
    </form>
  );
}
